import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  Users,
  Award,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ArrowLeft,
  Shield,
  Mountain,
  Camera,
  Banknote,
  Sparkles,
  Phone,
  MessageCircle,
  Mail,
  ListChecks,
  X,
} from "lucide-react";
import ClientLayout from "../../components/Layout/ClientLayout";
import {
  useGetProgrammesDetailQuery,
  useGetProgrammesQuery,
} from "../../apiCaller/apis/getApi";
import { GetTableListAPI } from "../../apiCaller/getAPIsCaller";
import HtmlContent from "../../utils/DisplayHTML";
import DateFormatterComponent from "../../utils/DateFormatterComponent";
import ExploreProgrammeCard from "./components/ExploreProgrammeCard";
import TimelineRedesign from "./TimelineRedesign";
import PriceGridCards from "./PriceComparison";
import Program2 from "../../assets/images/banners/program2.jpg";

// Phase Card Minimal Component - Shows adventures inline
const PhaseCardMinimal = ({ phase, phaseNumber }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const hasAdventures =
    phase.programme_adventures && phase.programme_adventures.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: phaseNumber * 0.1 }}
      className="bg-gradient-to-br from-white to-mamba-bg-light rounded-3xl overflow-hidden border-2 border-mamba-sky-blue/20 shadow-lg hover:shadow-xl transition-all duration-300"
    >
      {/* Phase Header - Clickable */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-gradient-to-r from-slate-50 to-mamba-bg-light border-b-2 border-mamba-sky-blue/20 p-6 md:p-8 cursor-pointer hover:from-mamba-bg-light hover:to-slate-50 transition-all duration-300 group"
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-mamba-sky-blue to-mamba-navy shadow-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
            <span className="text-2xl font-black font-montserrat text-white">
              {String(phaseNumber).padStart(2, "0")}
            </span>
          </div>
          <div className="flex-1">
            <h3 className="text-2xl md:text-3xl font-black font-montserrat text-mamba-navy mb-1 group-hover:text-mamba-sky-blue transition-colors">
              {phase.name}
            </h3>
            {hasAdventures && (
              <p className="text-slate-600 text-sm font-medium">
                {phase.programme_adventures.length}{" "}
                {phase.programme_adventures.length === 1
                  ? "Adventure"
                  : "Adventures"}
              </p>
            )}
          </div>
          {/* Collapse/Expand Icon */}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="flex-shrink-0"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-mamba-sky-blue to-mamba-navy shadow-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
              <ChevronDown className="w-6 h-6 text-white" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Phase Content - Collapsible */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-6 md:p-8">
              {/* Description */}
              {phase.description && (
                <div className="prose prose-slate max-w-none mb-6 text-slate-700 font-inter">
                  <HtmlContent htmlData={phase.description} />
                </div>
              )}

              {/* Adventures List - Minimalist Inline Display */}
              {hasAdventures && (
                <div className="mt-10">
                  <div className="flex items-center gap-2 mb-6">
                    <Calendar className="w-5 h-5 text-mamba-golden" />
                    <h4 className="text-lg font-bold font-montserrat text-mamba-navy">
                      Phase Adventures
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {phase.programme_adventures.map((adventure, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.05 }}
                        className="group bg-white rounded-2xl p-6 border-2 border-mamba-sky-blue/20 hover:border-mamba-golden/50 hover:shadow-lg transition-all duration-300"
                      >
                        {/* Adventure Icon & Name */}
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-mamba-sky-blue/20 to-mamba-navy/20 flex items-center justify-center flex-shrink-0">
                            <Mountain className="w-6 h-6 text-mamba-sky-blue" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-base font-bold font-montserrat text-mamba-navy mb-2 group-hover:text-mamba-sky-blue transition-colors line-clamp-2">
                              {adventure?.adventure_data?.destination_data
                                ?.name ||
                                adventure.name ||
                                "Adventure"}
                            </h5>
                            {adventure?.adventure_data?.start_date && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-slate-400" />
                                <span className="text-sm text-slate-600 font-medium">
                                  {new Date(
                                    adventure?.adventure_data?.start_date,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ProgrammeDetailRedesign = () => {
  const [expandedPhases, setExpandedPhases] = useState({});
  const [showPricingBanner, setShowPricingBanner] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    description: true,
    whatToExpect: true,
    gallery: true,
    pricing: true,
    phases: true,
    timeline: true,
    exploreMore: true,
  });
  const navigate = useNavigate();
  const { programmeId } = useParams();

  const { data: programmes, isLoading: programmesIsLoading } = GetTableListAPI(
    {},
    useGetProgrammesQuery,
  );

  const { data: programme, isLoading: programmeIsLoading } = GetTableListAPI(
    { programmeId: programmeId },
    useGetProgrammesDetailQuery,
  );

  useEffect(() => {
    if (programme?.phases) {
      const allExpanded = {};
      programme.phases.forEach((phase) => {
        allExpanded[phase.id] = true;
      });
      setExpandedPhases(allExpanded);
    }

    // Show pricing banner after a delay
    if (programme) {
      const timer = setTimeout(() => {
        setShowPricingBanner(true);
      }, 2000); // Show after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [programme]);

  const togglePhase = (phaseId) => {
    setExpandedPhases((prev) => ({
      ...prev,
      [phaseId]: !prev[phaseId],
    }));
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  if (programmeIsLoading) {
    return (
      <ClientLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex p-6 bg-mamba-sky-blue/10 rounded-full mb-4">
              <Mountain className="w-16 h-16 text-mamba-sky-blue animate-pulse" />
            </div>
            <p className="text-mamba-navy text-lg font-montserrat">
              Loading programme details...
            </p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  // Get the lowest price for the banner
  const getLowestPrice = () => {
    if (!programme?.price_tiers || programme.price_tiers.length === 0)
      return null;
    let lowestPrice = null;
    let currency = "";

    programme.price_tiers.forEach((group) => {
      group.tiers?.forEach((tier) => {
        const amount = tier.amount;
        if (!lowestPrice || amount < lowestPrice) {
          lowestPrice = amount;
          currency = tier.currency;
        }
      });
    });

    return lowestPrice ? { amount: lowestPrice, currency } : null;
  };

  const lowestPrice = getLowestPrice();

  return (
    <ClientLayout>
      {/* Sticky Pricing Banner */}
      <AnimatePresence>
        {showPricingBanner && (lowestPrice || programme?.booking_link) && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3, type: "spring", damping: 25 }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-mamba-sky-blue/20 shadow-2xl"
          >
            <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
              <div className="flex items-center justify-between gap-4">
                {/* Left: Price Info */}
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="hidden sm:flex w-12 h-12 rounded-xl bg-gradient-to-br from-mamba-sky-blue to-mamba-navy items-center justify-center">
                    {lowestPrice ? (
                      <Banknote className="w-6 h-6 text-white" />
                    ) : (
                      <Mountain className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    {lowestPrice ? (
                      <>
                        <p className="text-xs text-slate-600 font-medium font-montserrat">
                          Starting from
                        </p>
                        <p className="text-xl sm:text-2xl font-black bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent font-montserrat">
                          {lowestPrice.currency} {lowestPrice.amount}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-slate-600 font-medium font-montserrat">
                          {programme?.name}
                        </p>
                        <p className="text-xl sm:text-2xl font-black bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent font-montserrat">
                          Book Your Adventure
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Right: CTA + Close */}
                <div className="flex items-center gap-2 sm:gap-3">
                  <a
                    href={programme.booking_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-bold font-montserrat rounded-full shadow-lg hover:shadow-xl transition-all text-sm sm:text-base"
                    >
                      Book Now
                    </motion.button>
                  </a>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowPricingBanner(false)}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <div className="relative h-[600px] lg:h-[700px] overflow-hidden">
        {/* Background Image with Animation */}
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${programme?.display_image_url})`,
            }}
          />
        </motion.div>

        {/* Mamba Navy Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/90 via-slate-800/70 to-mamba-sky-blue/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/95 via-transparent to-transparent" />

        {/* Animated Background Elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-mamba-golden/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-20 w-40 h-40 bg-mamba-sky-blue/10 rounded-full blur-3xl animate-pulse" />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl"
          >
            {/* Back Button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              onClick={() => navigate(-1)}
              className="absolute top-8 left-8 inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-montserrat font-semibold hover:bg-white/20 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </motion.button>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 mb-6 px-5 py-2 bg-mamba-golden/20 backdrop-blur-md border border-mamba-golden/40 rounded-full">
              <Sparkles className="w-5 h-5 text-mamba-golden" />
              <span className="text-sm text-white font-montserrat font-bold tracking-wider uppercase">
                Training Program
              </span>
            </div>

            {/* Title */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black font-montserrat text-white mb-6 tracking-tight leading-tight">
              {programme?.name}
            </h1>

            {/* Meta Info */}
            {/* <div className="flex flex-wrap items-center justify-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-400" />
                <span className="font-medium">
                  <DateFormatterComponent date={programme?.start_date} />
                </span>
              </div>
              <span className="text-white/40">→</span>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-teal-400" />
                <span className="font-medium">
                  <DateFormatterComponent date={programme?.end_date} />
                </span>
              </div>
              <span className="text-white/40">|</span>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                <span className="font-medium">
                  {programme?.duration} {programme?.duration_units}
                </span>
              </div>
            </div> */}
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-white/70 text-sm font-medium">Explore</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
            >
              <div className="w-1.5 h-1.5 bg-white rounded-full" />
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="bg-white">
        {/* Quick Info Cards */}
        <div className="px-5 md:px-10 lg:px-16 xl:px-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-32 mb-20 relative z-20"
          >
            {[
              {
                icon: Calendar,
                label: "Start Date",
                value: <DateFormatterComponent date={programme?.start_date} />,
                gradient: "from-white to-mamba-bg-light",
                iconColor: "text-mamba-sky-blue",
              },
              {
                icon: Calendar,
                label: "End Date",
                value: <DateFormatterComponent date={programme?.end_date} />,
                gradient: "from-white to-mamba-bg-light",
                iconColor: "text-mamba-golden",
              },
              {
                icon: Clock,
                label: "Duration",
                value: `${programme?.duration} ${programme?.duration_units}`,
                gradient: "from-white to-mamba-bg-light",
                iconColor: "text-mamba-navy",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -8 }}
                className={`bg-gradient-to-br ${item.gradient} rounded-3xl p-8 shadow-lg border-2 border-mamba-sky-blue/20`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-gradient-to-br from-mamba-sky-blue to-mamba-navy rounded-2xl shadow-md">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-navy/70">
                    {item.label}
                  </div>
                </div>
                <div className="text-2xl font-bold font-montserrat text-mamba-navy">
                  {item.value}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Description Section */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="px-5 md:px-20 lg:px-32 xl:px-64 mb-20"
        >
          <div className="max-w-8xl mx-auto">
            <div
              className="text-center mb-12 cursor-pointer"
              onClick={() => toggleSection("description")}
            >
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent rounded-full" />
                <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-sky-blue">
                  About This Program
                </span>
                <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent rounded-full" />
              </div>
              <div className="flex items-center justify-center gap-4">
                <h2 className="text-4xl md:text-5xl font-black font-montserrat text-mamba-navy leading-tight">
                  What You'll
                  <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                    Experience
                  </span>
                </h2>
                <motion.div
                  animate={{ rotate: expandedSections.description ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <div className="p-2 rounded-full bg-mamba-sky-blue/10 hover:bg-mamba-sky-blue/20 transition-colors">
                    <ChevronDown className="w-8 h-8 text-mamba-sky-blue" />
                  </div>
                </motion.div>
              </div>
            </div>

            <AnimatePresence>
              {expandedSections.description && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border-2 border-mamba-sky-blue/20"
                >
                  <div className="prose prose-lg max-w-none text-slate-700 font-inter leading-relaxed">
                    <HtmlContent htmlData={programme?.description} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.section>

        {/* Image Banner 1 */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="relative h-64 md:h-80 mb-20 overflow-hidden"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${Program2})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-mamba-navy/95 via-slate-900/90 to-mamba-sky-blue/90" />
          <div className="relative h-full flex items-center justify-center px-6">
            <div className="text-center text-white max-w-3xl">
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-black font-montserrat mb-4 drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]">
                Ready for an Adventure?
              </h3>
              <p className="text-lg md:text-xl text-white font-dancing italic drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                Join us on this incredible journey
              </p>
            </div>
          </div>
        </motion.section>

        {/* What to Expect Section */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="px-5 md:px-20 lg:px-32 xl:px-64 mb-20"
        >
          <div className="">
            {/* Section Header */}
            <div
              className="text-center mb-12 cursor-pointer"
              onClick={() => toggleSection("whatToExpect")}
            >
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent rounded-full" />
                <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-sky-blue">
                  Program Highlights
                </span>
                <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent rounded-full" />
              </div>
              <div className="flex items-center justify-center gap-4">
                <h2 className="text-4xl md:text-5xl font-black font-montserrat text-mamba-navy leading-tight">
                  What to
                  <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                    Expect
                  </span>
                </h2>
                <motion.div
                  animate={{ rotate: expandedSections.whatToExpect ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <div className="p-2 rounded-full bg-mamba-sky-blue/10 hover:bg-mamba-sky-blue/20 transition-colors">
                    <ChevronDown className="w-8 h-8 text-mamba-sky-blue" />
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Content */}
            <AnimatePresence>
              {expandedSections.whatToExpect && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="grid md:grid-cols-2 gap-6"
                >
                  {[
                    {
                      icon: Mountain,
                      title: "Stunning Views",
                      text: "Breathtaking mountain trails and scenic panoramas",
                    },
                    {
                      icon: Users,
                      title: "Expert Guidance",
                      text: "Professional guides and supportive community",
                    },
                    {
                      icon: Shield,
                      title: "Safety First",
                      text: "Comprehensive safety measures and quality equipment",
                    },
                    {
                      icon: Award,
                      title: "Certification",
                      text: "Receive a certificate upon successful completion",
                    },
                  ].map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      whileHover={{ y: -4 }}
                      className="bg-gradient-to-br from-white to-mamba-bg-light rounded-2xl p-8 border-2 border-mamba-sky-blue/20 hover:border-mamba-golden/40 transition-all shadow-md hover:shadow-xl"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-mamba-sky-blue to-mamba-navy shadow-lg flex items-center justify-center">
                          <item.icon className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold font-montserrat text-mamba-navy mb-2">
                            {item.title}
                          </h3>
                          <p className="text-slate-600 font-inter">
                            {item.text}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.section>

        {/* Gallery Section - Redesigned */}
        {programme?.images && programme.images.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="px-5 md:px-20 lg:px-32 xl:px-64 mb-20"
          >
            <div className="">
              {/* Section Header */}
              <div
                className="text-center mb-12 cursor-pointer"
                onClick={() => toggleSection("gallery")}
              >
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent rounded-full" />
                  <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-golden">
                    Visual Journey
                  </span>
                  <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent rounded-full" />
                </div>
                <div className="flex items-center justify-center gap-4">
                  <h2 className="text-4xl md:text-5xl font-black font-montserrat text-mamba-navy leading-tight">
                    Adventure
                    <span className="block bg-gradient-to-r from-mamba-golden to-amber-400 bg-clip-text text-transparent">
                      Gallery
                    </span>
                  </h2>
                  <motion.div
                    animate={{ rotate: expandedSections.gallery ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <div className="p-2 rounded-full bg-mamba-golden/10 hover:bg-mamba-golden/20 transition-colors">
                      <ChevronDown className="w-8 h-8 text-mamba-golden" />
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Gallery Grid */}
              <AnimatePresence>
                {expandedSections.gallery && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  >
                    {programme.images.map((image, idx) => {
                      // Cycle through gradient options
                      const gradients = [
                        "from-mamba-navy/80 to-mamba-sky-blue/80",
                        "from-mamba-sky-blue/80 to-mamba-golden/80",
                        "from-mamba-golden/80 to-mamba-navy/80",
                        "from-mamba-navy/80 to-mamba-golden/80",
                      ];
                      const gradient = gradients[idx % gradients.length];

                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.15, duration: 0.6 }}
                          className="relative group cursor-pointer overflow-hidden rounded-3xl h-80 md:h-96 lg:h-[350px]"
                        >
                          {/* Image */}
                          <img
                            src={image.url}
                            alt={image.caption || `Gallery image ${idx + 1}`}
                            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-1"
                          />

                          {/* Always Visible Gradient Overlay */}
                          <div
                            className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-40 group-hover:opacity-60 transition-opacity duration-500`}
                          />

                          {/* Content - Only show if caption exists */}
                          {image.caption && (
                            <div className="absolute inset-0 flex flex-col justify-end p-6">
                              <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                transition={{ delay: idx * 0.15 + 0.3 }}
                              >
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center group-hover:bg-white/30 transition-colors">
                                    <Camera className="w-5 h-5 text-white" />
                                  </div>
                                  <div className="h-px flex-1 bg-white/30" />
                                </div>
                                <p className="text-white font-bold font-montserrat text-lg md:text-xl">
                                  {image.caption}
                                </p>
                              </motion.div>
                            </div>
                          )}

                          {/* Hover Zoom Icon */}
                          <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            whileHover={{ scale: 1, opacity: 1 }}
                            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl"
                          >
                            <svg
                              className="w-6 h-6 text-blue-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                              />
                            </svg>
                          </motion.div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.section>
        )}

        {/* Pricing Section */}
        {(programme?.price_tiers?.length > 0 ||
          programme?.payment_installments?.length > 0) && (
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-mamba-bg-light via-white to-mamba-bg-light px-5 md:px-20 lg:px-32 xl:px-64 py-20 mb-20"
          >
            <div className="max-w-8xl mx-auto">
              {/* Pricing Content - Two Column Layout: Prices Left, Image Right */}
              <div className="grid lg:grid-cols-2 gap-8">
                {/* LEFT COLUMN: Section Header + Complete Pricing */}
                <div className="space-y-8">
                  {/* Section Header */}
                  <div
                    className="text-left cursor-pointer"
                    onClick={() => toggleSection("pricing")}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-1 w-12 bg-gradient-to-r from-mamba-golden to-transparent rounded-full" />
                      <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-golden">
                        Investment
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <h2 className="text-4xl md:text-5xl font-black font-montserrat text-mamba-navy leading-tight">
                        Pricing &
                        <span className="block bg-gradient-to-r from-mamba-golden to-amber-400 bg-clip-text text-transparent">
                          Payment Plans
                        </span>
                      </h2>
                      <motion.div
                        animate={{ rotate: expandedSections.pricing ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex-shrink-0"
                      >
                        <div className="p-2 rounded-full bg-mamba-golden/10 hover:bg-mamba-golden/20 transition-colors">
                          <ChevronDown className="w-8 h-8 text-mamba-golden" />
                        </div>
                      </motion.div>
                    </div>
                  </div>
                  {/* Pricing Tiers */}
                  {expandedSections.pricing &&
                    programme?.price_tiers &&
                    programme.price_tiers.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="bg-gradient-to-br from-white to-mamba-bg-light rounded-3xl p-8 md:p-10 shadow-xl border-2 border-mamba-navy/30"
                      >
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-8">
                          <div className="w-12 h-12 bg-gradient-to-br from-mamba-navy to-slate-700 rounded-full flex items-center justify-center ring-2 ring-mamba-navy/10">
                            <Banknote className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-black font-montserrat text-mamba-navy">
                              Pricing Options
                            </h3>
                            <p className="text-sm text-mamba-navy/70 font-inter">
                              Choose the package that fits you
                            </p>
                          </div>
                        </div>

                        {/* Pricing Packages using PriceGridCards */}
                        <PriceGridCards
                          data={programme.price_tiers}
                          theme="mamba"
                        />

                        {/* Pricing Includes Info - Popup Trigger */}
                        {programme?.pricing_includes && (
                          <button
                            onClick={() => setShowPricingModal(true)}
className="mt-6 w-full p-4 bg-gradient-to-br from-mamba-sky-blue/20 to-mamba-sky-blue/10 rounded-xl border-2 border-mamba-sky-blue/40 hover:border-mamba-sky-blue hover:shadow-lg hover:from-mamba-sky-blue/30 hover:to-mamba-sky-blue/15 transition-all flex items-center justify-between group shadow-sm"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-mamba-sky-blue to-mamba-navy rounded-lg flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                                <ListChecks className="w-5 h-5 text-white" />
                              </div>
                              <span className="text-base font-black font-montserrat text-mamba-navy">
                                What's Included
                              </span>
                            </div>
                            <div className="w-8 h-8 bg-mamba-sky-blue/20 rounded-full flex items-center justify-center shadow-sm group-hover:bg-mamba-sky-blue/30 transition-all">
                              <ArrowRight className="w-5 h-5 text-mamba-sky-blue" />
                            </div>
                          </button>
                        )}
                      </motion.div>
                    )}

                  {/* Payment Plan - Still on Left Column */}
                  {expandedSections.pricing &&
                    programme?.payment_installments &&
                    programme.payment_installments.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="bg-gradient-to-br from-mamba-bg-light to-white rounded-3xl p-8 md:p-10 border-2 border-mamba-navy/30 shadow-xl"
                      >
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-8">
                          <div className="w-12 h-12 bg-gradient-to-br from-mamba-navy to-slate-700 rounded-full flex items-center justify-center ring-2 ring-mamba-navy/10">
                            <Calendar className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-black font-montserrat text-mamba-navy">
                              Payment Plan
                            </h3>
                            <p className="text-sm text-mamba-navy/70 font-inter">
                              Flexible installment options
                            </p>
                          </div>
                        </div>

                        {/* Progress Timeline */}
                        <div className="relative mb-10 px-2">
                          {/* Background Line */}
                          <div className="absolute left-0 top-1/2 w-full h-2 bg-gradient-to-r from-mamba-navy/10 to-mamba-navy/20 -translate-y-1/2 rounded-full"></div>

                          {/* Progress Line */}
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{
                              width: `${(100 / programme.payment_installments.length) * (programme.payment_installments.length - 1)}%`,
                            }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                            className="absolute left-0 top-1/2 h-2 bg-gradient-to-r from-mamba-golden via-amber-400 to-mamba-golden -translate-y-1/2 rounded-full shadow-lg"
                          />

                          {/* Milestone Dots */}
                          <div className="flex justify-between relative z-10">
                            {programme.payment_installments.map(
                              (installment, index) => (
                                <motion.div
                                  key={index}
                                  initial={{ scale: 0, opacity: 0 }}
                                  whileInView={{ scale: 1, opacity: 1 }}
                                  viewport={{ once: true }}
                                  transition={{
                                    delay: index * 0.15,
                                    type: "spring",
                                    stiffness: 200,
                                  }}
                                  className="flex flex-col items-center"
                                >
                                  <div className="w-12 h-12 bg-gradient-to-br from-mamba-navy to-slate-700 rounded-full flex items-center justify-center font-black font-montserrat text-white text-sm border-4 border-white shadow-xl ring-2 ring-mamba-navy/20">
                                    {index + 1}
                                  </div>
                                  <div className="mt-3 text-xs font-bold font-montserrat text-mamba-navy uppercase text-center min-w-[60px] max-w-[80px]">
                                    {installment.installment_name}
                                  </div>
                                </motion.div>
                              ),
                            )}
                          </div>
                        </div>

                        {/* Installments Cards */}
                        <div className="space-y-3">
                          {programme.payment_installments.map(
                            (installment, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ scale: 1.02, x: 4 }}
                                className="group flex items-center justify-between bg-white p-5 rounded-2xl border-2 border-mamba-navy/30 hover:border-mamba-navy hover:shadow-md transition-all"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-gradient-to-br from-mamba-navy/20 to-slate-700/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <span className="text-lg font-black font-montserrat text-mamba-navy">
                                      {index + 1}
                                    </span>
                                  </div>
                                  <span className="font-bold font-montserrat text-mamba-navy text-sm uppercase tracking-wide">
                                    {installment.installment_name}
                                  </span>
                                </div>
                                <div className="flex items-baseline gap-1">
                                  <span className="text-3xl font-black font-montserrat text-mamba-navy">
                                    {installment.percentage}
                                  </span>
                                  <span className="text-sm font-bold text-mamba-navy/70">
                                    %
                                  </span>
                                </div>
                              </motion.div>
                            ),
                          )}
                        </div>
                      </motion.div>
                    )}
                </div>

                {/* RIGHT COLUMN: Image with Wavy Border + Contact CTA */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="relative lg:sticky lg:top-8 h-[600px] lg:h-[800px]"
                >
                  {/* Wavy Image with CTA Overlay */}
                  <div className="relative w-full h-full">
                    {/* Mountain Image with Wavy Left Border on large screens, rounded border on small */}
                    <div
                      className="w-full h-full relative rounded-2xl lg:rounded-none overflow-hidden"
                      style={{
                        clipPath: "var(--clip-path-wavy-programme)",
                      }}
                    >
                      <style>{`
                        @media (min-width: 1024px) {
                          :root {
                            --clip-path-wavy-programme: url(#wavyLeftProgramme);
                          }
                        }
                        @media (max-width: 1023px) {
                          :root {
                            --clip-path-wavy-programme: none;
                          }
                        }
                      `}</style>
                      <img
                        src={
                          programme?.display_image_url ||
                          "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80"
                        }
                        alt="Mountain adventure"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/85 to-mamba-sky-blue/80" />

                      {/* Contact CTA Card */}
                      <div className="absolute inset-0 flex items-center justify-center p-6 md:p-8">
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.3, duration: 0.6 }}
                          className="bg-white rounded-3xl p-8 md:p-10 max-w-lg w-full shadow-2xl border-4 border-mamba-golden/30"
                        >
                          {/* Badge */}
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-mamba-golden/20 border border-mamba-golden/40 mb-4">
                            <Sparkles className="w-4 h-4 text-mamba-golden" />
                            <span className="text-xs font-black uppercase tracking-wider text-mamba-navy font-montserrat">
                              Ready to Start?
                            </span>
                          </div>

                          {/* Heading */}
                          <h3 className="text-3xl lg:text-4xl font-black font-montserrat text-mamba-navy leading-tight mb-3">
                            Talk to a
                            <span className="block text-mamba-sky-blue">
                              Mamba Guide
                            </span>
                          </h3>

                          {/* Description */}
                          <p className="text-base text-slate-600 font-inter mb-6 leading-relaxed">
                            Program details, payment plans, and bookings. Reach
                            out and we'll guide you through every step.
                          </p>

                          {/* Contact Buttons - Compact Row */}
                          <div className="flex gap-3">
                            <a
                              href="tel:+254712345678"
                              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-bold font-montserrat text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all"
                            >
                              <Phone className="w-4 h-4" />
                              Call
                            </a>
                            <a
                              href="https://wa.me/254712345678"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white font-bold font-montserrat text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all"
                            >
                              <MessageCircle className="w-4 h-4" />
                              WhatsApp
                            </a>
                            <a
                              href="mailto:info@mambahikers.com?subject=Programme%20Inquiry"
                              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-mamba-navy text-white font-bold font-montserrat text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all"
                            >
                              <Mail className="w-4 h-4" />
                              Email
                            </a>
                          </div>
                        </motion.div>
                      </div>
                    </div>

                    {/* Hidden SVG for Wavy Clip Path */}
                    <svg width="0" height="0" style={{ position: "absolute" }}>
                      <defs>
                        <clipPath
                          id="wavyLeftProgramme"
                          clipPathUnits="objectBoundingBox"
                        >
                          <path d="M1,0 L0.2,0 C0.05,0.15 0.35,0.25 0.2,0.45 C0.05,0.65 0.35,0.75 0.2,1 L1,1 Z" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                </motion.div>
              </div>
              {/* CTA Button - Full Width */}
              {programme?.booking_link && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="col-span-full mt-4"
                >
                  <a
                    href={programme.booking_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <motion.button
                      whileHover={{ scale: 1.02, y: -4 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full px-12 py-6 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy text-xl font-black font-montserrat rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 uppercase tracking-wide"
                    >
                      <span className="flex items-center justify-center gap-3">
                        <Banknote className="w-6 h-6" />
                        Book Your Adventure Now
                        <ArrowRight className="w-6 h-6" />
                      </span>
                    </motion.button>
                  </a>
                </motion.div>
              )}
            </div>
          </motion.section>
        )}

        {/* Phases Section - Redesigned with inline adventures */}
        {programme?.has_phases && programme?.phases?.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="px-5 md:px-20 lg:px-32 xl:px-64 mb-20"
          >
            <div className="">
              {/* Section Header */}
              <div
                className="text-center mb-12 cursor-pointer"
                onClick={() => toggleSection("phases")}
              >
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent rounded-full" />
                  <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-sky-blue">
                    Program Structure
                  </span>
                  <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent rounded-full" />
                </div>
                <div className="flex items-center justify-center gap-4">
                  <h2 className="text-4xl md:text-5xl font-black font-montserrat text-mamba-navy leading-tight">
                    {programme.phases.length} Progressive
                    <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                      Phases
                    </span>
                  </h2>
                  <motion.div
                    animate={{ rotate: expandedSections.phases ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <div className="p-2 rounded-full bg-mamba-sky-blue/10 hover:bg-mamba-sky-blue/20 transition-colors">
                      <ChevronDown className="w-8 h-8 text-mamba-sky-blue" />
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Phases List */}
              <AnimatePresence>
                {expandedSections.phases && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {programme.phases.map((phase, index) => (
                      <PhaseCardMinimal
                        key={phase.id}
                        phase={phase}
                        phaseNumber={index + 1}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.section>
        )}

        {/* Image Banner 2 */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="relative h-96 mb-20 overflow-hidden"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200)`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-mamba-navy/95 via-slate-900/90 to-mamba-sky-blue/85" />
          <div className="relative h-full flex items-center justify-center px-6">
            <div className="text-center text-white max-w-4xl">
              <h3 className="text-4xl md:text-5xl lg:text-6xl font-black font-montserrat mb-4 drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]">
                Your Journey Awaits
              </h3>
              <p className="text-xl text-white font-dancing italic drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                Experience breathtaking adventures and create unforgettable
                memories
              </p>
            </div>
          </div>
        </motion.section>

        {/* Unified Adventure Timeline Section - For ALL programmes */}
        {((programme?.has_phases && programme?.phases?.length > 0) ||
          (!programme?.has_phases &&
            programme?.programme_adventures?.length > 0)) && (
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="px-5 md:px-10 lg:px-16 xl:px-32 mb-20"
          >
            <div className="max-w-7xl mx-auto">
              {/* Section Header */}
              <div
                className="text-center mb-12 cursor-pointer"
                onClick={() => toggleSection("timeline")}
              >
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent rounded-full" />
                  <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-golden">
                    Journey Path
                  </span>
                  <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-golden to-transparent rounded-full" />
                </div>
                <div className="flex items-center justify-center gap-4">
                  <h2 className="text-4xl md:text-5xl font-black font-montserrat text-mamba-navy leading-tight">
                    Adventure
                    <span className="block bg-gradient-to-r from-mamba-golden to-amber-400 bg-clip-text text-transparent">
                      Timeline
                    </span>
                  </h2>
                  <motion.div
                    animate={{ rotate: expandedSections.timeline ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <div className="p-2 rounded-full bg-mamba-golden/10 hover:bg-mamba-golden/20 transition-colors">
                      <ChevronDown className="w-8 h-8 text-mamba-golden" />
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Timeline Content */}
              <AnimatePresence>
                {expandedSections.timeline && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TimelineRedesign
                      adventures={
                        programme?.has_phases
                          ? programme.phases.flatMap(
                              (phase) => phase.programme_adventures || [],
                            )
                          : programme?.programme_adventures
                      }
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.section>
        )}

        {/* Other Programmes */}
        {programmes?.results && programmes.results.length > 1 && (
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-b from-slate-50 to-white py-20"
          >
            <div className="px-5 md:px-20 lg:px-32 xl:px-64">
              {/* Section Header */}
              <div
                className="text-center mb-12 cursor-pointer"
                onClick={() => toggleSection("exploreMore")}
              >
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent rounded-full" />
                  <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-sky-blue">
                    More Adventures
                  </span>
                  <div className="h-1 w-16 bg-gradient-to-r from-transparent via-mamba-sky-blue to-transparent rounded-full" />
                </div>
                <div className="flex items-center justify-center gap-4">
                  <h2 className="text-4xl md:text-5xl font-black font-montserrat text-mamba-navy leading-tight">
                    Explore More
                    <span className="block bg-gradient-to-r from-mamba-sky-blue to-mamba-navy bg-clip-text text-transparent">
                      Programs
                    </span>
                  </h2>
                  <motion.div
                    animate={{ rotate: expandedSections.exploreMore ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <div className="p-2 rounded-full bg-mamba-sky-blue/10 hover:bg-mamba-sky-blue/20 transition-colors">
                      <ChevronDown className="w-8 h-8 text-mamba-sky-blue" />
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Programs Grid */}
              <AnimatePresence>
                {expandedSections.exploreMore && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                  >
                    {programmes.results
                      .filter((p) => p.id !== programme?.id)
                      .slice(0, 8)
                      .map((prog, index) => (
                        <ExploreProgrammeCard
                          key={prog.id}
                          programme={prog}
                          index={index}
                          handleClick={(slug) => navigate(`/programmes/${slug}`)}
                        />
                      ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.section>
        )}
      </div>

      {/* What's Included Popup Modal */}
      <AnimatePresence>
        {showPricingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
            onClick={() => setShowPricingModal(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Modal Content - Centered */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <ListChecks className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold font-montserrat text-white">
                    What's Included
                  </h3>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowPricingModal(false)}
                  className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </motion.button>
              </div>

              {/* Body */}
              <div className="p-8 md:p-10 overflow-y-auto max-h-[calc(90vh-88px)]">
                <div className="prose prose-lg max-w-none text-slate-700 prose-headings:text-mamba-navy prose-headings:font-montserrat prose-li:marker:text-mamba-sky-blue prose-p:leading-relaxed">
                  <HtmlContent htmlData={programme?.pricing_includes} />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ClientLayout>
  );
};

export default ProgrammeDetailRedesign;
